import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { withSnackbar } from 'notistack';

import { makeStyles } from '@material-ui/styles';
import { CircularProgress } from '@material-ui/core';

import { decodeJwtPayload } from '~/utils/jwt';
import userRequests from '~/api/user';

import {
  setUserData,
  fetchMe,
  requestFeatureFlags,
  requestMWLinksLength,
  signOut,
  fetchUserPermissions,
} from '~/thunks';

import { getEmail, getIsAuthorized, getIsSubApp } from '~/selectors';
import {
  isPostMessageOriginAllowed,
  LocalStorage,
  checkIsIframe,
} from '~/helpers';

import { useAppContext } from '~/application-context';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
  },
}));

const AuthProvider = ({ enqueueSnackbar, children }) => {
  const publicPaths = ['/auth/', '/public/'];
  const dispatch = useDispatch();
  const isSubApp = useSelector(getIsSubApp);

  const [isLoading, setLoading] = React.useState(false);
  const [loadingMandatoryInfo, setLoadingMandatoryInfo] = React.useState(false);

  const history = useHistory();
  const classes = useStyles();
  const appContext = useAppContext();

  if (
    !isSubApp &&
    !LocalStorage.getToken() &&
    !RegExp(publicPaths.join('|')).test(history.location.pathname)
  ) {
    history.push('/auth/login');
  }

  const isAuthorized = useSelector(getIsAuthorized);
  const userEmail = useSelector(getEmail);

  const handleMwEventLogin = useCallback(
    async ({ admin_email: adminEmail, apiKey }) => {
      if (isAuthorized) {
        dispatch(signOut());
      }

      const { token, user } = await userRequests.signIn({
        apiKey,
        adminEmail,
      });

      LocalStorage.setToken(token);
      LocalStorage.setUser(user);

      dispatch(
        setUserData({
          ...user,
          isAuthorized: true,
        })
      );
      await new Promise((res) => setTimeout(res, 2000));

      dispatch(fetchMe({ enqueueSnackbar, history }));

      history.push(history.location.pathname);
    },
    [isAuthorized, dispatch, enqueueSnackbar, history]
  );

  useEffect(() => {
    if (appContext?.mwApiKey) {
      (async () => {
        try {
          setLoading(true);

          const token = LocalStorage.getToken();
          const tokenContent = decodeJwtPayload(token);

          const reauthenticateUser = async () => {
            LocalStorage.removeToken();
            LocalStorage.removeUser();

            await handleMwEventLogin({
              admin_email: appContext?.mwAdminEmail || null,
              apiKey: appContext?.mwApiKey,
            });
          };

          if (tokenContent?.email === appContext?.mwUserEmail) {
            dispatch(
              fetchMe({
                enqueueSnackbar,
                history,
                onError: () =>
                  reauthenticateUser()
                    .then(() => true)
                    .catch(() => false),
              })
            );

            history.push(history.location.pathname);

            // Lazy regenerate token
            handleMwEventLogin({
              admin_email: appContext?.mwAdminEmail || null,
              apiKey: appContext?.mwApiKey,
            }).catch((err) => {
              console.error(err);
            });

            return;
          }

          await reauthenticateUser();
        } finally {
          setLoading(false);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appContext?.mwApiKey,
    appContext?.mwAdminEmail,
    // appContext?.mwUserEmail,
    enqueueSnackbar,
    dispatch,
    history,
  ]);

  useEffect(() => {
    const listener = async ({ origin, data: { event, payload } }) => {
      // Important security check
      if (!isPostMessageOriginAllowed(origin) || !checkIsIframe()) return;

      // eslint-disable-next-line no-console
      console.info('MW event on OMNI', { event, payload });

      if (event === 'login') {
        await handleMwEventLogin(payload);
      } else {
        // eslint-disable-next-line no-console
        console.error('Unhandled MW event on OMNI:', payload);
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [handleMwEventLogin]);

  const rehydrate = useCallback(() => {
    const userInLocalStorage = LocalStorage.getUser();
    const token = LocalStorage.getToken();

    if (token === null || userInLocalStorage === null) {
      return;
    }

    dispatch(
      setUserData({
        ...userInLocalStorage,
        isAuthorized: true,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        if (!isAuthorized) {
          rehydrate();

          return;
        }

        setLoadingMandatoryInfo(true);

        await dispatch(requestFeatureFlags());

        await dispatch(fetchUserPermissions());

        await dispatch(requestMWLinksLength(enqueueSnackbar));
      } catch (error) {
        console.error(error);
        enqueueSnackbar(error.message, { variant: 'error' });
      } finally {
        setLoadingMandatoryInfo(false);
      }
    })();
  }, [dispatch, isAuthorized, enqueueSnackbar, rehydrate, userEmail]);

  if (isLoading || loadingMandatoryInfo) {
    return (
      <div className={classes.root}>
        <CircularProgress />
      </div>
    );
  }

  return <>{children}</>;
};

export default withSnackbar(AuthProvider);
